<script setup lang="ts">
import { computed } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { UiButton, UiCard, UiSwitch, UiText, useApi, useToaster, type ProjectReviewStatus } from '@shopexperts/shared';
import { extractServerMessage } from '@/app/composables/claim/claimApiErrors';
import type { ProjectStatus } from '@/v2Projects/types';

const props = defineProps<{ projectId: number; actingOrgId: number; projectStatus: ProjectStatus; status: ProjectReviewStatus }>();
const api = useApi();
const queryClient = useQueryClient();
const toaster = useToaster();

const settings = useMutation({
  mutationFn: (enabled: boolean) => api.expert.projectsV2.updateReviewSettings({
    params: { projectId: props.projectId },
    body: { acting_org_id: props.actingOrgId, auto_review_requests_enabled: enabled },
  }),
  onSuccess: async ({ auto_review_requests_enabled: enabled }) => {
    toaster.create({ title: enabled ? 'Automatic review requests turned on' : 'Automatic review requests turned off', type: 'success' });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['v2projects', 'review'] }),
      queryClient.invalidateQueries({ queryKey: ['v2projects', 'detail', props.projectId] }),
    ]);
  },
  onError: (error: Error) => {
    toaster.create({ title: 'Could not update review requests', description: error.message || 'Please try again.', type: 'error' });
  },
});

const SEND_ERRORS: Record<string, string> = {
  already_requested: 'A review request has already been sent for this project.',
  already_reviewed: 'The client has already reviewed this project.',
  not_eligible: 'Review requests can only be sent for completed projects.',
  review_emails_disabled: 'Review request emails are paused right now. Please try again later.',
};

const send = useMutation({
  mutationFn: () => api.expert.projectsV2.sendReviewRequest({
    params: { projectId: props.projectId },
    body: { acting_org_id: props.actingOrgId },
  }),
  onSuccess: () => {
    toaster.create({ title: 'Review request sent', description: 'We emailed your client a link to review this project.', type: 'success' });
  },
  onError: (error: unknown) => {
    const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
    const description = (code && SEND_ERRORS[code]) || extractServerMessage(error) || 'Please try again.';
    toaster.create({ title: 'Could not send the review request', description, type: 'error' });
  },
  // Success and "already requested" both change what the server reports.
  onSettled: () => queryClient.invalidateQueries({ queryKey: ['v2projects', 'review'] }),
});

const formatDate = (value: string) => new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(value));
const sentText = computed(() => {
  const request = props.status.review_request;
  if (!request) return '';
  const sent = `Review request sent ${formatDate(request.sent_at)}.`;
  return request.reminder_sent_at ? `${sent} Reminder sent ${formatDate(request.reminder_sent_at)}.` : sent;
});
</script>

<template>
  <UiCard>
    <div class="flex flex-col gap-3">
      <UiText as="h3" variant="h6" text="Review requests" />
      <UiSwitch
        label="Ask for a review automatically"
        :model-value="status.auto_review_requests_enabled"
        :disabled="settings.isPending.value"
        @update:model-value="settings.mutate($event)"
      />
      <UiText
        as="p"
        variant="p-small"
        class="text-se-gray-700"
        :text="status.auto_review_requests_enabled
          ? 'When the client completes this project, we prompt them and email a review request with one reminder.'
          : 'No review popup or emails for this project. The client still sees the review banner.'"
      />
      <div class="flex flex-col gap-2 border-t border-se-gray-200 pt-3">
        <UiText v-if="sentText" as="p" variant="p-small" class="text-se-gray-700" :text="sentText" />
        <template v-else-if="status.can_send_review_request">
          <UiText as="p" variant="p-small" class="text-se-gray-700" text="Email your client the review request now." />
          <UiButton variant="outline" label="Send review request" :disabled="send.isPending.value" @click="send.mutate()" />
        </template>
        <UiText
          v-else-if="projectStatus !== 'completed'"
          as="p"
          variant="p-small"
          class="text-se-gray-700"
          text="You can send a review request once the client completes the project."
        />
      </div>
    </div>
  </UiCard>
</template>
